import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import {
  Dialog,
  TextField,
  Button,
  Grid,
  Typography,
  Autocomplete,
  TextareaAutosize,
  darken,
  Alert,
} from "@mui/material";

import { Props, State } from "./interfaces";

import {
  DialogContent,
  AbsenceActionsContainer,
  ActionValidationContainer,
  ConfirmationMessageMainContainer,
  ConfirmationMessageContainer,
  ConfirmationMessageBlockContainer,
} from "./styles";

import isEqual from "lodash/isEqual";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";
import { isValidDate } from "../../../utils/time-utils";
import DialogTitle from "../../DialogTitle/DialogTitle";
import DialogActions from "../../DialogActions/DialogActions";
import ConfirmationChangesDialog from "../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import { TnAAbsenceDTO, TnAAbsenceSTO } from "../../../models/tna";
import { CustomDate } from "../../../models/custom-date/custom-date";

import {
  approveAbsense,
  approveRejectCancellationRequest,
  saveAbsence,
  TnAAbsencesApiService,
} from "../../../services/tna-absences/tna-absences-api.service";
import CustomConfirmationChangesDialog from "../../CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { TnAAbsenceTypesApiService } from "../../../services/tna-absence-types/tna-absence-types-api.service";
import { TnAAbsenceTypesFilter } from "../../../models/tna/tna-absence-types/tna-absence-types-filter";
import AttachedDocuments from "../../../containers/AttachedDocuments/AttachedDocuments";
import AddNewDocument from "../../../containers/AddNewDocument/AddNewDocument";
import { DOCUMENT_CATEGORIES, DOCUMENT_TYPES } from "../../../utils/constants/constants";
import { DocumentCategoryRef } from "../../../models/document-categories";
import { DocumentTypeRef } from "../../../models/document-types";
import { DocumentsType } from "../../../containers/AddTransaction/AddEditTransaction/interfaces";
import { DocumentDTO, DocumentRef } from "../../../models/documents";
import { saveDocument } from "../../../services/documents";
import { handleUploadNewFiles } from "../../../containers/Document/Document/utils";
import { INewFile } from "../../../containers/Document/Document/interfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "@mui/material/styles";
import { isAuthorizedEeGroup } from "../../../custom-hooks/useAuthorization/useAuthorization";
import { useAppSelector } from "../../../custom-hooks/redux/redux";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { intlFormatDateShort } from "../../../utils/time-utils";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeOverlappingAbsencesApiService } from "../../../services/employee-overlapping-absences/employee-overlapping-absences-api.service";
import { OverlappingAbsencesFilter } from "../../../models/tna/tna-overlapping-absences/overlapping-absences-filter";
import { toast } from "react-toastify";

const AddEditAbsence = ({
  employee,
  effect = "add",
  item,
  handleNotifyParent,
  fromDate,
  toDate,
  children,
  showChildren = false,
  isEditable = true,
  viewMode = false,
  minDate,
  maxDate,
  forManager = false,
  allowedToApproveReject = false,
  allowedOnlyApproveReject = false,
  showAdditionalInfoMessage = false,
  additionalInfoMessage,
  allowedOnlyManagersAction = false,
  parentIsLoading = false,
  errorFromParent,
  counters,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_absence";

  const theme = useTheme();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  //#region can approve/reject absence
  const canApproveReject = useMemo(
    () =>
      isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves],
        currentUserAuthorizations,
        employee?.groups?.map((group) => group?.id) ?? [],
        true
      ),
    [currentUserAuthorizations, employee?.groups]
  );
  //#endregion

  //#region default item
  const DEFAULT_ITEM: Partial<TnAAbsenceDTO> = useMemo(() => {
    return {
      companyAccount: item?.companyAccount ?? employee?.companyAccount ?? null,
      legalEntity: item?.legalEntity ?? employee?.legalEntity ?? null,
      employee: item?.employee ?? employee,
      fromDate:
        item?.fromDate ??
        fromDate ??
        CustomDate.FromDate(!!minDate ? (new Date() > minDate ? new Date() : minDate) : new Date()),
      toDate:
        item?.toDate ??
        toDate ??
        CustomDate.FromDate(!!minDate ? (new Date() > minDate ? new Date() : minDate) : new Date()),
      absenceType: item?.absenceType ?? null,
      comment: item?.comment ?? "",
      isApproved: item?.isApproved ?? false,
      isRejected: item?.isRejected ?? false,
      isCancelled: item?.isCancelled ?? false,
      cancelRequested: item?.cancelRequested ?? false,
      cancelRequestedAt: item?.cancelRequestedAt ?? null,
      cancellationRequestApproved: item?.cancellationRequestApproved ?? null,
      documentation: item?.documentation ?? null,
      isVisibleToColleagues: !!item ? item.isVisibleToColleagues : false,
    };
  }, [employee, item, minDate, maxDate, fromDate, toDate]);
  //#endregion

  //#region initial state
  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      absence: item ?? (DEFAULT_ITEM as TnAAbsenceDTO),
      isCCDOpen: false,
      areFieldsValidated: effect === "edit",
      mutatingStatus: "",
      confirmStatus: false,
      mutatingStatusValue: false,
      isConfirmationDialogOpen: false,
      confirmationDialogTitle: "",
      confirmationDialogMessage: <></>,
      confirmationDialogActionsAndHandlers: [],
      documents: { document: null, newFiles: [] },
      requestedAbsenceCancelation: false,
      approveRejectRequestCancellation: "",
      error: "",
      overlappingAbsences: [],
      isCOADOpen: false,
    };
  }, [effect, DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });
  //#endregion

  const setOpenDialog = useCallback(
    (value: boolean) => isEditable && setState((state) => ({ ...state, isDialogOpen: value })),
    [isEditable]
  );
  //#endregion

  //#region handleChangeField
  const handleChangeField = useCallback((field: keyof TnAAbsenceDTO, value: TnAAbsenceDTO[keyof TnAAbsenceDTO]) => {
    setState((state) => ({
      ...state,
      absence: {
        ...state.absence,
        [field]: value,
      },
    }));
  }, []);
  //#endregion

  //#region handleChangeDocument
  const handleChangeDocumentCallback = (documents: DocumentsType): void => {
    setState((state) => ({
      ...state,
      documents: {
        ...documents,
        document: documents.document,
        newFiles: documents.newFiles,
      },
    }));
  };
  // #endregion

  //#region save absence document
  const saveAbsenceDocument = async (absence: TnAAbsenceSTO, document: DocumentRef) => {
    const absenceToSave = { ...absence, documentation: document };

    const response = await new TnAAbsencesApiService().saveItems([absenceToSave]);

    toastifyResponse(response, "failed");

    return response;
  };
  //#endregion

  //#region handleSaveDocument
  const handleSaveDocument = async (
    absence: TnAAbsenceDTO,
    document: DocumentDTO,
    newFiles: INewFile[],
    correlationID?: string | null
  ) => {
    const response = await saveDocument(document, correlationID);

    const invokerId = response.invokerId;
    const correlationId = response.correlationId;

    await handleUploadNewFiles(document, newFiles, invokerId, correlationId);

    await saveAbsenceDocument(absence, document);
  };
  // #endregion

  //#region check overlapping absences
  const checkOverlappingAbsences = useCallback(async () => {
    const response = await new EmployeeOverlappingAbsencesApiService().loadItems({
      employeeId: state.absence!.employee!.id,
      fromDate: state.absence.fromDate,
      toDate: state.absence.toDate,
    } as OverlappingAbsencesFilter);

    return response;
  }, [state.absence]);
  // #endregion

  // #region load overlapping absences
  const loadOverlappingAbsences = useCallback(async () => {
    setState((state) => ({ ...state, loading: true, error: "" }));

    try {
      const response = await checkOverlappingAbsences();

      if (!response.error) {
        setState((state) => ({
          ...state,
          loading: false,
          overlappingAbsences: response.items,
        }));
      } else {
        setState((state) => ({
          ...state,
          loading: false,
          error: response.error,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
        error: error as string,
      }));
    }
  }, [checkOverlappingAbsences]);
  // #endregion

  //#region save absences
  const saveAbsences = useCallback(async () => {
    const absenceId = item?.id ?? uuidv4();

    const response = await new TnAAbsencesApiService().saveItems([
      {
        ...state.absence,
        id: absenceId,
      } as TnAAbsenceSTO,
    ]);

    if (!response.error) {
      if (state.documents.document) {
        await handleSaveDocument(
          {
            ...state.absence,
            id: absenceId,
          },
          {
            ...state.documents.document,
            documentDate: item?.documentation?.documentDate ?? new Date(),
          },
          state.documents.newFiles,
          response.correlationId
        );
      }

      effect === "add" && setState({ ...INITIAL_STATE });
      effect === "edit" &&
        setState((state) => ({
          ...state,
          isDialogOpen: false,
          loading: false,
          isCCDOpen: false,
        }));
      handleNotifyParent();
    } else {
      if (response.isSucceeded) {
        effect === "add" && setState({ ...INITIAL_STATE });
        effect === "edit" &&
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            isCCDOpen: false,
          }));
        handleNotifyParent();
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }
    }

    response.isSucceeded && !!response.error
      ? toast.warning(`${t(`${tRP}.fields.error_after_saving_absence`)}. ${response.error}`, { autoClose: 5000 })
      : toastifyResponse(response);
  }, [INITIAL_STATE, effect, handleNotifyParent, state.absence, state.documents]);
  // #endregion

  //#region handleSave
  const handleSave = useCallback(
    async (ignoreOverlapping: boolean = false) => {
      setState((state) => ({ ...state, loading: true, error: "" }));

      try {
        // if (ignoreOverlapping) {
        await saveAbsences();
        // }

        // if (!ignoreOverlapping) {
        //   const response = await checkOverlappingAbsences();

        //   if (!response.error) {
        //     if (!!response.items.length) {
        //       setState((state) => ({
        //         ...state,
        //         loading: false,
        //         overlappingAbsences: response.items,
        //         isCOADOpen: true,
        //       }));
        //     }

        //     if (!response.items.length) {
        //       await saveAbsences();
        //     }
        //   } else {
        //     setState((state) => ({
        //       ...state,
        //       loading: false,
        //       error: response.error,
        //     }));
        //   }
        // }
      } catch (error) {
        setState((state) => ({
          ...state,
          loading: false,
          error: error as string,
        }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    },
    [checkOverlappingAbsences, saveAbsences, state.overlappingAbsences]
  );
  // #endregion

  //#region handleCancelConfirmationDialog
  const handleCancelConfirmationDialog = () => {
    setState((state) => ({
      ...state,
      isConfirmationDialogOpen: false,
      confirmationDialogTitle: "",
      confirmationDialogMessage: <></>,
      confirmationDialogActionsAndHandlers: [],
    }));
  };
  //#endregion

  //#region handleDiscardChanges
  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);
  //#endregion

  //#region resetAndCloseDialog
  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.absence, INITIAL_STATE.absence)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };
  //#endregion

  //#region handleSaveStatus
  const handleSaveStatus = useCallback(async () => {
    if (state.confirmStatus) {
      if (!!item?.id) {
        setState((state) => ({
          ...state,
          loading: true,
          error: "",
        }));

        try {
          const absenceToSave =
            state.approveRejectRequestCancellation === "reject"
              ? {
                  ...state.absence,
                }
              : state.requestedAbsenceCancelation
              ? {
                  ...state.absence,
                  cancelRequested: true,
                  cancelRequestedAt: new Date(),
                }
              : {
                  ...state.absence,
                  ...{ [`${state.mutatingStatus}`]: !state.mutatingStatusValue },
                  isRejected:
                    state.mutatingStatus === "isApproved" && !state.mutatingStatusValue
                      ? false
                      : state.mutatingStatus === "isRejected"
                      ? !state.mutatingStatusValue
                      : state.absence.isRejected,

                  isApproved:
                    state.mutatingStatus === "isRejected" && !state.mutatingStatusValue
                      ? false
                      : state.mutatingStatus === "isApproved"
                      ? !state.mutatingStatusValue
                      : state.absence.isApproved,
                  id: item?.id,
                };

          const apiService = new TnAAbsencesApiService();
          apiService.useSaveItemsPath(`/CancellationRequestTnAAbsences`);

          const response =
            state.mutatingStatus === "isRejected" || state.mutatingStatus === "isApproved"
              ? await approveAbsense(
                  absenceToSave.employee?.id as string,
                  absenceToSave.id,
                  absenceToSave.isApproved ? "isApproved" : "isRejected"
                )
              : state.requestedAbsenceCancelation
              ? await apiService.saveItems([absenceToSave])
              : !!state.approveRejectRequestCancellation
              ? await approveRejectCancellationRequest(
                  absenceToSave.employee?.id as string,
                  absenceToSave.id,
                  state.approveRejectRequestCancellation === "approve" ? "isApproved" : "isRejected"
                )
              : await saveAbsence(absenceToSave);

          const error = response.error;

          if (!error) {
            setState({ ...INITIAL_STATE });

            handleNotifyParent();
          } else {
            setState((state) => ({
              ...state,
              error: response.error as string,
            }));
          }
        } catch (error) {
          setState({ ...INITIAL_STATE });
        } finally {
          setState((state) => ({
            ...state,
            loading: false,
            isConfirmationDialogOpen: false,
          }));
        }
      }
    }
  }, [
    state.confirmStatus,
    state.mutatingStatus,
    state.mutatingStatusValue,
    item?.id,
    handleNotifyParent,
    state.absence,
    state.requestedAbsenceCancelation,
    state.approveRejectRequestCancellation,
  ]);
  //#endregion

  // #region handleConfirmationCallback
  const handleConfirmationCallback = useCallback(
    (requestedAbsenceCancelation?: boolean, approveRejectRequestCancellation?: "approve" | "reject") => {
      setState((state) => ({
        ...state,
        confirmStatus: true,
        requestedAbsenceCancelation: !!requestedAbsenceCancelation,
        approveRejectRequestCancellation: approveRejectRequestCancellation ?? "",
      }));
    },
    []
  );
  // #endregion

  useEffect(() => {
    state.confirmStatus && handleSaveStatus();
  }, [state.confirmStatus, handleSaveStatus]);

  useEffect(() => {
    effect === "add" &&
      setState((state) => ({
        ...state,
        absence: {
          ...state.absence,
          legalEntity: employee?.legalEntity ?? null,
          companyAccount: employee?.companyAccount ?? null,
          employee: employee,
        },
      }));
  }, [effect, employee]);

  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.absence.employee && !!state.absence.absenceType && !!state.absence.fromDate && !!state.absence.toDate
    );
  }, [state.absence]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.absence, areFieldsValidated]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        absence: item,
      }));

    return () => {
      if (effect === "add") setState({ ...INITIAL_STATE });
    };
  }, [item, effect, INITIAL_STATE]);

  // #region isAbsenceSignedOff
  const isAbsenceSignedOff = useMemo(() => {
    return (
      item?.fromDate &&
      minDate &&
      new Date(item?.fromDate.year, item.fromDate.month - 1, item.fromDate.day) < new Date(minDate)
    );
  }, [item, minDate]);
  // #endregion

  // useEffect(() => {
  //   state.isDialogOpen && loadOverlappingAbsences();
  // }, [state.absence.fromDate, state.absence.toDate, state.isDialogOpen]);

  return (
    <>
      {effect === "edit" && item ? (
        <Grid onClick={() => setOpenDialog(true)}>{children}</Grid>
      ) : showChildren ? (
        <Grid onClick={() => setOpenDialog(true)}>{children}</Grid>
      ) : (
        <Button onClick={() => setOpenDialog(true)}>{`${t(`${tRP}.title`)}`}</Button>
      )}

      <Dialog
        open={state.isDialogOpen}
        onClose={(event: any, reason) => {
          if (reason === "backdropClick") {
            event.stopPropagation();
          }
          resetAndCloseDialog();
        }}
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle
          title={effect === "add" ? t(`${tRP}.title`) : t(`${tRP}.edit_title`)}
          handleClose={resetAndCloseDialog}
        />
        <LoadingOverlay loading={parentIsLoading}>
          <DialogContent>
            {!!showAdditionalInfoMessage && !!additionalInfoMessage && !parentIsLoading && (
              <Grid sx={{ width: "100%", marginBottom: "10px" }}>
                <Alert severity="info">{`${additionalInfoMessage}`}</Alert>
              </Grid>
            )}

            {(!!item?.isApproved || !!item?.isRejected) &&
              forManager &&
              allowedToApproveReject &&
              canApproveReject &&
              !isAbsenceSignedOff &&
              !!item?.id &&
              !parentIsLoading && (
                <Grid sx={{ width: "100%", marginBottom: "10px" }}>
                  <Alert severity="info">{`Absence already ${item?.isApproved ? "approved" : "rejected"}`}</Alert>
                </Grid>
              )}

            <Autocomplete
              aria-required={true}
              disabled
              value={employee}
              getOptionLabel={(option) => option?.name ?? ""}
              options={[employee]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${t(`common_words.employee`)}`}
                />
              )}
            />

            <AutocompleteDropdown
              labelKey={t(`${tRP}.fields.absence_type`)}
              serviceApi={new TnAAbsenceTypesApiService()}
              propsItem={state.absence}
              field="absenceType"
              handleChange={handleChangeField}
              propsFilter={
                {
                  legislationIds: !!employee?.legalEntity?.legislation?.id
                    ? [employee?.legalEntity?.legislation?.id]
                    : [],
                  availableToLegalEntityId: employee?.legalEntity?.id ?? "",
                } as TnAAbsenceTypesFilter
              }
              disabled={
                viewMode ||
                allowedOnlyApproveReject ||
                !!isAbsenceSignedOff ||
                !!item?.isApproved ||
                !!item?.isRejected ||
                !!item?.isCancelled ||
                allowedOnlyManagersAction
              }
              translationRoutePath="absence_types"
            />

            <DesktopDatePicker
              inputFormat="dd.MM.yyyy"
              mask="__.__.____"
              label={`${t(`${tRP}.fields.from_date`)}`}
              value={CustomDate.ToDate(state.absence.fromDate)}
              onChange={(newValue) => {
                if (isValidDate(newValue)) {
                  handleChangeField("fromDate", CustomDate.FromDate(newValue as Date));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                />
              )}
              minDate={!allowedOnlyApproveReject ? minDate : CustomDate.ToDate(state.absence.fromDate)}
              maxDate={!allowedOnlyApproveReject ? maxDate : CustomDate.ToDate(state.absence.fromDate)}
              disabled={
                viewMode ||
                allowedOnlyApproveReject ||
                !!isAbsenceSignedOff ||
                !!item?.isApproved ||
                !!item?.isRejected ||
                !!item?.isCancelled ||
                allowedOnlyManagersAction
              }
            />

            <DesktopDatePicker
              inputFormat="dd.MM.yyyy"
              mask="__.__.____"
              label={`${t(`${tRP}.fields.to_date`)}`}
              value={CustomDate.ToDate(state.absence.toDate)}
              onChange={(newValue) => {
                if (isValidDate(newValue)) {
                  handleChangeField("toDate", CustomDate.FromDate(newValue as Date));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                />
              )}
              minDate={!allowedOnlyApproveReject ? minDate : CustomDate.ToDate(state.absence.toDate)}
              maxDate={!allowedOnlyApproveReject ? maxDate : CustomDate.ToDate(state.absence.toDate)}
              disabled={
                viewMode ||
                allowedOnlyApproveReject ||
                !!isAbsenceSignedOff ||
                !!item?.isApproved ||
                !!item?.isRejected ||
                !!item?.isCancelled ||
                allowedOnlyManagersAction
              }
            />

            {/* <Grid sx={{ width: "100%", display: "flex", flexDirection: "row-reverse" }}>
              <OverlappingColleguesAbsences
                overlappingAbsences={state.overlappingAbsences}
                employeeAbsence={state.absence}
                handleSaveAbsence={() => handleSave()}
                handleChangeField={handleChangeField}
                minDate={!allowedOnlyApproveReject ? minDate : CustomDate.ToDate(state.absence.toDate)}
                maxDate={!allowedOnlyApproveReject ? maxDate : CustomDate.ToDate(state.absence.toDate)}
                disabledDates={
                  viewMode ||
                  allowedOnlyApproveReject ||
                  !!isAbsenceSignedOff ||
                  !!item?.isApproved ||
                  !!item?.isRejected ||
                  !!item?.isCancelled ||
                  allowedOnlyManagersAction
                }
                parentIsLoading={state.loading}
                disabledSaveButton={
                  !areFieldsValidated() ||
                  viewMode ||
                  allowedOnlyApproveReject ||
                  !!isAbsenceSignedOff ||
                  !!item?.isApproved ||
                  !!item?.isRejected
                }
              />
            </Grid> */}

            <Grid style={{ width: "100%" }}>
              <Typography style={{ opacity: 0.7 }}>{`${t(`${tRP}.fields.comment`)}`}</Typography>
              <TextareaAutosize
                minRows={3}
                maxRows={5}
                style={{ width: "100%" }}
                value={state.absence.comment ?? ""}
                onChange={({ target: { value } }) => handleChangeField("comment", value)}
                disabled={
                  viewMode ||
                  allowedOnlyApproveReject ||
                  !!isAbsenceSignedOff ||
                  !!item?.isApproved ||
                  !!item?.isRejected ||
                  !!item?.isCancelled ||
                  allowedOnlyManagersAction
                }
              />
            </Grid>

            <Grid
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                "&>div": { width: "45%" },
              }}
            >
              {!DEFAULT_ITEM?.documentation &&
                !isAbsenceSignedOff &&
                !viewMode &&
                !(!!item?.isApproved || !!item?.isRejected || !!item?.isCancelled || allowedOnlyManagersAction) && (
                  <Grid sx={{ display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}>
                    <AddNewDocument
                      isWidget
                      openButtonKey={`${t(`${tRP}.fields.add_document`)}`}
                      handleConfirm={handleChangeDocumentCallback}
                      filesProps={state.documents.newFiles}
                      documentFieldsProps={{
                        legalEntity: state.absence?.legalEntity ?? null,
                        category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
                        type: DOCUMENT_TYPES.AbsenceDocumentation as DocumentTypeRef,
                        employee: state.absence.employee,
                        documentDate: new Date(),
                      }}
                    />

                    {state.documents.document && (
                      <Grid>
                        <FontAwesomeIcon
                          icon={faCheck}
                          color={theme.palette.success.main}
                          size={"2x"}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}

              {!!DEFAULT_ITEM?.documentation && (
                <Grid>
                  <AttachedDocuments
                    documentIDs={[DEFAULT_ITEM?.documentation?.id]}
                    count={DEFAULT_ITEM?.documentation?.filesCount}
                    buttonSize="2x"
                    handleNotifyParent={handleNotifyParent}
                    documentsReadOnlyFields={[
                      {
                        documentID: DEFAULT_ITEM?.documentation?.id,
                        readOnlyFields: {
                          isEmployeeRO: true,
                          isCategoryRO: true,
                          isLegalEntityRO: true,
                          isTypeRO: true,
                          isDateRO: true,
                          isStatusRO: true,
                        },
                      },
                    ]}
                    hideReadOnlyFields={true}
                    useSimpleView={
                      !!isAbsenceSignedOff ||
                      !!item?.isApproved ||
                      !!item?.isRejected ||
                      !!item?.isCancelled ||
                      viewMode ||
                      allowedOnlyApproveReject ||
                      allowedOnlyManagersAction
                    }
                    documentIsReadOnly={
                      !!isAbsenceSignedOff ||
                      !!item?.isApproved ||
                      !!item?.isRejected ||
                      !!item?.isCancelled ||
                      viewMode ||
                      allowedOnlyApproveReject ||
                      allowedOnlyManagersAction
                    }
                  />
                </Grid>
              )}

              {/* <Grid>
                <FormControlLabel
                  key={"isVisibleToColleagues"}
                  control={
                    <Checkbox
                      checked={state.absence.isVisibleToColleagues}
                      onChange={(_, checked: boolean) => handleChangeField("isVisibleToColleagues", checked)}
                    />
                  }
                  label={`${t(`${tRP}.fields.visible_to_colleagues`)}`}
                  labelPlacement="end"
                />
              </Grid> */}
            </Grid>

            {!!isAbsenceSignedOff && !parentIsLoading && (
              <Grid sx={{ width: "100%", marginBottom: "10px" }}>
                {forManager ? (
                  <Alert severity="info">{`${t(`${tRP}.signed_off_message_for_manager`)}`}</Alert>
                ) : (
                  <Alert severity="info">{`${t(`${tRP}.signed_off_message`)}`}</Alert>
                )}
              </Grid>
            )}

            {!!errorFromParent && !parentIsLoading && (
              <Grid sx={{ width: "100%", marginBottom: "10px" }}>
                <Alert severity="info">{errorFromParent}</Alert>
              </Grid>
            )}

            {effect === "edit" && !!item && !!counters?.length && forManager && (
              <Grid sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "5px" }}>
                {counters
                  .filter((counter) => counter.year === item.fromDate?.year || counter.year === item.toDate?.year)
                  .map((counter) => {
                    return (
                      <React.Fragment key={counter?.id}>
                        <Grid
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography sx={{ fontWeight: "bold" }}>In {counter.year}</Typography>
                          <Typography>
                            {`${t(`${tRP}.total_vacation_balance`)}`}: {counter.balance.toFixed(2)}
                          </Typography>
                          <Typography>
                            {`${t(`${tRP}.used_planned`)}`}: {counter.usedOrPlanned.toFixed(2)}
                          </Typography>
                        </Grid>
                      </React.Fragment>
                    );
                  })}
              </Grid>
            )}

            {!!state.absence.absenceType && !viewMode && (
              <>
                <AbsenceActionsContainer>
                  <ActionValidationContainer>
                    {forManager &&
                      allowedToApproveReject &&
                      canApproveReject &&
                      !!item?.id &&
                      !item?.isApproved &&
                      !item?.isRejected &&
                      !item?.isCancelled &&
                      [
                        ...(!isAbsenceSignedOff
                          ? [
                              {
                                action: "reject",
                                field: "isRejected",
                                stateField: state.absence.isRejected,
                              },
                            ]
                          : []),
                        {
                          action: "approve",
                          field: "isApproved",
                          stateField: state.absence.isApproved,
                        },
                      ].map(({ action, field, stateField }, idx) => (
                        <Button
                          key={idx}
                          color={field === "isApproved" ? "success" : "warning"}
                          variant={"contained"}
                          disabled={stateField}
                          onClick={() => {
                            setState((state) => ({
                              ...state,
                              mutatingStatus: field,
                              mutatingStatusValue: stateField,
                              isConfirmationDialogOpen: true,
                              confirmationDialogTitle: t(`${tRP}.confirmation_dialog.titles.${action}`),
                              confirmationDialogMessage: (
                                <Typography>{`${t(`${tRP}.confirmation_dialog.messages.${action}`)}`}</Typography>
                              ),
                              confirmationDialogActionsAndHandlers: [
                                {
                                  action: t(`common_words.yes`),
                                  buttonVariant: "contained",
                                  handler: handleConfirmationCallback,
                                },
                              ],
                            }));
                          }}
                        >
                          {t(`${tRP}.status_buttons.${action}`)}
                        </Button>
                      ))}
                  </ActionValidationContainer>

                  <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: "10px" }}>
                    {/* button to cancel absence */}
                    {!!item?.id &&
                      !isAbsenceSignedOff &&
                      !item?.isApproved &&
                      !item?.isRejected &&
                      !item?.isCancelled &&
                      (!forManager || (forManager && canApproveReject)) && (
                        <Button
                          sx={{
                            background: theme.palette.primary.main,
                          }}
                          variant={"contained"}
                          onClick={() => {
                            setState((state) => ({
                              ...state,
                              mutatingStatus: "isCancelled",
                              mutatingStatusValue: state.absence.isCancelled,
                              isConfirmationDialogOpen: true,
                              confirmationDialogTitle: t(`${tRP}.confirmation_dialog.titles.cancel_absence`),
                              confirmationDialogMessage: (
                                <Typography>{`${t(`${tRP}.confirmation_dialog.messages.cancel_absence`)}`}</Typography>
                              ),
                              confirmationDialogActionsAndHandlers: [
                                {
                                  action: t(`common_words.yes`),
                                  buttonVariant: "contained",
                                  handler: () => handleConfirmationCallback(false, undefined),
                                },
                              ],
                            }));
                          }}
                        >
                          {t(`${tRP}.status_buttons.cancel`)}
                        </Button>
                      )}

                    {/* button to restore absence */}
                    {!!item?.id &&
                      !isAbsenceSignedOff &&
                      !!item?.isCancelled &&
                      !item?.isApproved &&
                      !item?.isRejected &&
                      (!forManager || (forManager && canApproveReject)) && (
                        <Button
                          sx={{
                            background: theme.palette.primary.main,
                          }}
                          variant={"contained"}
                          onClick={() => {
                            setState((state) => ({
                              ...state,
                              mutatingStatus: "isCancelled",
                              mutatingStatusValue: state.absence.isCancelled,
                              isConfirmationDialogOpen: true,
                              confirmationDialogTitle: t(`${tRP}.confirmation_dialog.titles.restore`),
                              confirmationDialogMessage: (
                                <Typography>{`${t(`${tRP}.confirmation_dialog.messages.restore`)}`}</Typography>
                              ),
                              confirmationDialogActionsAndHandlers: [
                                {
                                  action: t(`common_words.yes`),
                                  buttonVariant: "contained",
                                  handler: () => handleConfirmationCallback(false, undefined),
                                },
                              ],
                            }));
                          }}
                        >
                          {t(`${tRP}.status_buttons.restore`)}
                        </Button>
                      )}

                    {/* button to approve request cancelation */}
                    {!!item?.id &&
                      !isAbsenceSignedOff &&
                      state.absence?.cancelRequested &&
                      state.absence?.cancellationRequestApproved === null &&
                      forManager &&
                      canApproveReject && (
                        <Button
                          sx={{
                            background: theme.palette.warning.light,

                            "&:hover": {
                              background: darken(theme.palette.warning.light, 0.15),
                            },
                          }}
                          variant={"contained"}
                          onClick={() => {
                            setState((state) => ({
                              ...state,
                              mutatingStatus: "isCancelled",
                              mutatingStatusValue: state.absence.isCancelled,
                              isConfirmationDialogOpen: true,
                              confirmationDialogTitle: t(
                                `${tRP}.confirmation_dialog.titles.approve_absence_cancellation`
                              ),
                              confirmationDialogMessage: (
                                <ConfirmationMessageMainContainer>
                                  <Typography>
                                    {`${t(
                                      `${tRP}.confirmation_dialog.messages.you_are_about_to_cancel_absence_request`
                                    )}`}
                                    :
                                  </Typography>
                                  <ConfirmationMessageContainer>
                                    <ConfirmationMessageBlockContainer>
                                      <Typography sx={{ fontWeight: "bold" }}>{`Employee:`}</Typography>
                                      <Typography>{`${state.absence.employee?.name ?? ""}`}</Typography>
                                    </ConfirmationMessageBlockContainer>
                                    <ConfirmationMessageBlockContainer>
                                      <Typography sx={{ fontWeight: "bold" }}>{`From`}</Typography>
                                      <Typography>{`${
                                        !!state.absence?.fromDate
                                          ? intlFormatDateShort(
                                              new Date(
                                                state.absence.fromDate.year,
                                                state.absence.fromDate.month - 1,
                                                state.absence.fromDate.day
                                              )
                                            )
                                          : ""
                                      }`}</Typography>
                                      <Typography sx={{ fontWeight: "bold" }}>{`to`}</Typography>
                                      <Typography>{`${
                                        !!state.absence?.toDate
                                          ? intlFormatDateShort(
                                              new Date(
                                                state.absence.toDate.year,
                                                state.absence.toDate.month - 1,
                                                state.absence.toDate.day
                                              )
                                            )
                                          : ""
                                      }`}</Typography>
                                    </ConfirmationMessageBlockContainer>
                                  </ConfirmationMessageContainer>
                                  <Typography>
                                    {`${t(`${tRP}.confirmation_dialog.messages.cancellation_consequence`)}`}
                                  </Typography>
                                </ConfirmationMessageMainContainer>
                              ),
                              confirmationDialogActionsAndHandlers: [
                                {
                                  action: t(`${tRP}.status_buttons.accept_absence_cancelation`),
                                  buttonVariant: "contained",
                                  handler: () => handleConfirmationCallback(false, "approve"),
                                },
                              ],
                            }));
                          }}
                        >
                          {t(`${tRP}.status_buttons.approve_cancellation`)}
                        </Button>
                      )}

                    {/* button to reject request absence cancellation */}
                    {!!item?.id &&
                      forManager &&
                      canApproveReject &&
                      state.absence.cancelRequested &&
                      state.absence.isApproved &&
                      state.absence.cancellationRequestApproved === null && (
                        <Button
                          // color={state.absence.isCancelled ? "primary" : "info"}
                          sx={{
                            background: theme.palette.error.light,

                            "&:hover": {
                              background: darken(theme.palette.error.light, 0.15),
                            },
                          }}
                          variant={"contained"}
                          onClick={() => {
                            setState((state) => ({
                              ...state,
                              isConfirmationDialogOpen: true,
                              confirmationDialogTitle: t(
                                `${tRP}.confirmation_dialog.titles.reject_absence_cancellation`
                              ),
                              confirmationDialogMessage:
                                state.absence.cancelRequested && state.absence.cancelRequestedAt ? (
                                  <ConfirmationMessageMainContainer>
                                    <Typography>
                                      {`${t(`${tRP}.confirmation_dialog.messages.you_are_about_to_reject_absence`)}`}
                                    </Typography>
                                    <ConfirmationMessageContainer>
                                      <ConfirmationMessageBlockContainer>
                                        <Typography sx={{ fontWeight: "bold" }}>{`Employee:`}</Typography>
                                        <Typography>{`${state.absence.employee?.name ?? ""}`}</Typography>
                                      </ConfirmationMessageBlockContainer>
                                      <ConfirmationMessageBlockContainer>
                                        <Typography sx={{ fontWeight: "bold" }}>{`From`}</Typography>
                                        <Typography>{`${
                                          !!state.absence?.fromDate
                                            ? intlFormatDateShort(
                                                new Date(
                                                  state.absence.fromDate.year,
                                                  state.absence.fromDate.month - 1,
                                                  state.absence.fromDate.day
                                                )
                                              )
                                            : ""
                                        }`}</Typography>
                                        <Typography sx={{ fontWeight: "bold" }}>{`to`}</Typography>
                                        <Typography>{`${
                                          !!state.absence?.toDate
                                            ? intlFormatDateShort(
                                                new Date(
                                                  state.absence.toDate.year,
                                                  state.absence.toDate.month - 1,
                                                  state.absence.toDate.day
                                                )
                                              )
                                            : ""
                                        }`}</Typography>
                                      </ConfirmationMessageBlockContainer>
                                    </ConfirmationMessageContainer>
                                    <Typography>{`${t(`${tRP}.confirmation_dialog.messages.proceed`)}`}</Typography>
                                  </ConfirmationMessageMainContainer>
                                ) : (
                                  <></>
                                ),
                              confirmationDialogActionsAndHandlers: [
                                {
                                  action: t(`${tRP}.status_buttons.reject_request_to_cancel_absence`),
                                  buttonVariant: "contained",
                                  handler: () => handleConfirmationCallback(false, "reject"),
                                },
                              ],
                            }));
                          }}
                        >
                          {t(`${tRP}.status_buttons.reject_cancellation`)}
                        </Button>
                      )}

                    {/* button for employee to request absence cancellation */}
                    {!!item?.id &&
                      !isAbsenceSignedOff &&
                      !forManager &&
                      !item?.isCancelled &&
                      (!item?.cancelRequestedAt ||
                        (item?.cancelRequested &&
                          item?.cancelRequestedAt &&
                          item?.cancellationRequestApproved === null)) &&
                      item?.isApproved && (
                        <Grid
                          title={
                            !!item?.cancelRequestedAt
                              ? `${t(`${tRP}.absence_cancellation_requested_on`)} ${intlFormatDateShort(
                                  new Date(item.cancelRequestedAt)
                                )}`
                              : `${t(`${tRP}.request_absence_cancellation`)}`
                          }
                        >
                          <Button
                            disabled={item?.cancelRequested}
                            sx={{
                              background: theme.palette.warning.light,

                              "&:hover": {
                                background: darken(theme.palette.warning.light, 0.15),
                              },
                            }}
                            variant={"contained"}
                            onClick={() => {
                              setState((state) => ({
                                ...state,
                                isConfirmationDialogOpen: true,
                                confirmationDialogTitle: t(
                                  `${tRP}.confirmation_dialog.titles.request_absence_cancelation`
                                ),
                                confirmationDialogMessage: (
                                  <Typography>{`${t(
                                    `${tRP}.confirmation_dialog.messages.request_absence_cancelation`
                                  )}`}</Typography>
                                ),
                                confirmationDialogActionsAndHandlers: [
                                  {
                                    action: t(`common_words.yes`),
                                    buttonVariant: "contained",
                                    handler: () => handleConfirmationCallback(true),
                                  },
                                ],
                              }));
                            }}
                          >
                            {t(`${tRP}.status_buttons.request_cancelation`)}
                          </Button>
                        </Grid>
                      )}
                  </Grid>
                </AbsenceActionsContainer>
              </>
            )}
          </DialogContent>
          {!allowedOnlyManagersAction && (
            <DialogActions
              handleSave={() => handleSave()}
              disabledSaveButton={
                !areFieldsValidated() ||
                viewMode ||
                allowedOnlyApproveReject ||
                !!isAbsenceSignedOff ||
                !!item?.isApproved ||
                !!item?.isRejected ||
                !!state.error
              }
              handleCancel={resetAndCloseDialog}
              loading={state.loading}
              error={state.error}
            />
          )}
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />

      <CustomConfirmationChangesDialog
        open={state.isConfirmationDialogOpen}
        loading={state.loading}
        title={state.confirmationDialogTitle}
        children={state.confirmationDialogMessage}
        handleCancel={handleCancelConfirmationDialog}
        actionsAndHandlers={state.confirmationDialogActionsAndHandlers}
        error={state.error}
      />

      <CustomConfirmationChangesDialog
        open={state.isCOADOpen}
        loading={state.loading}
        title={state.confirmationDialogTitle}
        children={state.confirmationDialogMessage}
        handleCancel={() => setState({ ...state, isCOADOpen: false })}
        actionsAndHandlers={[
          {
            action: t(`common_words.yes`),
            buttonVariant: "contained",
            handler: handleConfirmationCallback,
          },
        ]}
        error={state.error}
      />

      {/* <OverlappingConfirmationDialog
        open={state.isCOADOpen}
        loading={state.loading}
        handleCancel={() => setState({ ...state, isCOADOpen: false })}
        actionsAndHandlers={[
          {
            action: "Yes, save",
            buttonVariant: "contained",
            handler: () => handleSave(true),
          },
        ]}
        error={state.error}
        overlappingAbsences={state.overlappingAbsences}
        from={state.absence.fromDate as CustomDate}
        to={state.absence.toDate as CustomDate}
      /> */}
    </>
  );
};

export default React.memo(AddEditAbsence);
